import React from "react";
import { ChevronLeft, ChevronRight, Check, Quote } from "lucide-react";

/**
 * Auto-rotating testimonials carousel.
 * - Cycles every 6s, pauses on hover
 * - Keyboard arrows + dots navigation
 * - Public-safe fields only: title, category, outcome, rating, display_name
 */
export default function TestimonialsCarousel({ items, language }) {
  const [index, setIndex] = React.useState(0);
  const [paused, setPaused] = React.useState(false);
  const total = items.length;

  // Auto-rotate
  React.useEffect(() => {
    if (paused || total <= 1) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % total), 6000);
    return () => clearInterval(id);
  }, [paused, total]);

  const go = (n) => setIndex(((n % total) + total) % total);
  const prev = () => go(index - 1);
  const next = () => go(index + 1);

  // Keyboard nav
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, total]);

  if (total === 0) return null;
  const active = items[index];
  const outcomeColors = { good: "#00FF88", mixed: "#FFB800", bad: "#FF3B30" };
  const oColor = outcomeColors[active.outcome] || "#FFFFFF";

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="testimonials-carousel"
    >
      {/* Featured slide */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#121214] via-[#0F0F12] to-[#0A0A0A] border border-white/10 p-8 lg:p-14 min-h-[320px]">
        <div className="absolute top-6 right-6 opacity-10">
          <Quote className="w-32 h-32" strokeWidth={1} />
        </div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <span
              className="font-mono text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full"
              style={{ background: `${oColor}20`, color: oColor }}
              data-testid={`testimonial-outcome-${active.id}`}
            >
              {active.outcome}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
              {active.category}
            </span>
            <span className="flex items-center gap-0.5 ml-auto">
              {[1, 2, 3, 4, 5].map((n) => (
                <Check
                  key={n}
                  className={`w-3.5 h-3.5 ${n <= (active.rating || 0) ? "text-[#006CFF]" : "text-white/15"}`}
                  strokeWidth={2.5}
                />
              ))}
            </span>
          </div>

          <blockquote className="font-display font-semibold text-2xl lg:text-3xl leading-snug mb-6 text-white">
            "{active.title}"
          </blockquote>

          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-sm"
              style={{ background: `linear-gradient(135deg, #006CFF, #7C3AED)` }}
            >
              {(active.display_name || "B").charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="font-semibold text-white">{active.display_name || "BScanIA member"}</div>
              <div className="text-xs text-white/45 font-mono uppercase tracking-wider">
                {language === "es" ? "Miembro verificado" : "Verified member"}
              </div>
            </div>
          </div>
        </div>

        {/* Nav arrows */}
        {total > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous testimonial"
              data-testid="testimonial-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={next}
              aria-label="Next testimonial"
              data-testid="testimonial-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center text-white/70 hover:text-white transition-all"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Dots */}
      {total > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6" data-testid="testimonial-dots">
          {items.map((item, i) => (
            <button
              key={item.id || `dot-${i}`}
              onClick={() => go(i)}
              aria-label={`Go to testimonial ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-[#006CFF]" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
            />
          ))}
        </div>
      )}

      {/* Secondary grid: 3 next testimonials as thumbnails */}
      {total > 1 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-8">
          {[1, 2, 3].map((offset) => {
            const t = items[(index + offset) % total];
            if (!t || t.id === active.id) return null;
            const c = outcomeColors[t.outcome] || "#FFFFFF";
            return (
              <button
                key={t.id}
                onClick={() => go((index + offset) % total)}
                className="text-left p-4 rounded-xl bg-[#121214]/60 border border-white/5 hover:border-white/15 transition-all"
                data-testid={`testimonial-thumb-${t.id}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span
                    className="font-mono text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded-full"
                    style={{ background: `${c}18`, color: c }}
                  >
                    {t.outcome}
                  </span>
                </div>
                <p className="text-sm text-white/75 line-clamp-2 leading-snug">"{t.title}"</p>
                <div className="text-[10px] text-white/40 mt-2 font-mono uppercase tracking-wider">— {t.display_name}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
